import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from '@/shared/components/Spinner';
import { apiClient, ApiError } from '@/lib/api-client';
/** Si una sesión "caduca" antes de este margen tras crearla, algo va mal de
 *  verdad: no renovamos en bucle, mostramos el error (guardia del móvil). */
const MIN_SESSION_LIFETIME_MS = 15_000;
/** Placeholder A4 hasta conocer las dimensiones reales de la primera página. */
const DEFAULT_PAGE_RATIO = '1 / 1.4142';
/**
 * Maximum-security PDF viewer for STUDENTS. Pipeline:
 *
 *   1. POST /materials/:id/secure-session  — server returns a 256-bit master
 *      key + page count.
 *   2. For each page, GET /materials/:id/secure-page/:n  — AES-256-GCM
 *      ciphertext over a watermarked JPEG.
 *   3. A Web Worker holds the master key (out of the main heap), derives a
 *      per-page key via HKDF-SHA256 and decrypts → posts back the JPEG bytes.
 *   4. Main thread paints onto a <canvas> via createImageBitmap. No <img>,
 *      no Blob URL, no PDF.js, no native viewer toolbar.
 *
 *   + every page is rendered server-side as a JPEG with a diagonal
 *     watermark of the student email + timestamp baked into the bitmap, so
 *     even a screen capture is traceable.
 *   + DevTools "Save Response" on /secure-page returns just AES ciphertext.
 *   + window-level event listeners block contextmenu, copy/cut, drag,
 *     PrintScreen, Ctrl/⌘+S/P/C/X/A and beforeprint hides the document.
 *   + session expiry (30 min server-side) is handled silently, like the
 *     mobile viewer: a fully-loaded document keeps being readable forever
 *     (the painted canvases need no session), and a partially-loaded one
 *     renews the session under the hood shortly before expiry — students
 *     studying a material for over 30 minutes must never be kicked out.
 *   + WINDOWED PAINTING: a painted canvas retains an RGBA bitmap of ~8 MB
 *     (144 dpi A4). Keeping 20-40 pages painted at once blew past the canvas
 *     memory budget of mobile browsers (iOS Safari ≈ 200-380 MB) → blank
 *     pages or the tab being reloaded mid-study. Every decrypted JPEG
 *     (~200 KB) is kept in memory, but only the pages near the viewport are
 *     painted; leaving the window releases the bitmap, entering repaints it.
 *
 * Honest threat model: a determined developer with DevTools open can still
 * pluck the master key from the network response or hook into the worker.
 * The security gain is "raises the bar from one click to one afternoon",
 * combined with the un-removable visual watermark for traceability.
 */
export function SecureMaterialViewer({ materialId, title, onClose }) {
    const [session, setSession] = useState(null);
    const [loadedPages, setLoadedPages] = useState(0);
    const [error, setError] = useState(null);
    const [containerWidth, setContainerWidth] = useState(900);
    const workerRef = useRef(null);
    const containerRef = useRef(null);
    // One ref slot per page; wrappers/canvases are mounted reactively after session arrives.
    const wrapperRefs = useRef([]);
    const canvasRefs = useRef([]);
    // Windowed painting state (see header): decrypted JPEG per page, which
    // pages currently hold a bitmap, and which are near the viewport.
    const pageBlobsRef = useRef(new Map());
    const paintedRef = useRef(new Set());
    const inWindowRef = useRef(new Set());
    const defaultRatioRef = useRef(null);
    const ratioKnownRef = useRef(new Set());
    // Silent-renewal bookkeeping (mirrors the mobile viewer): loadedPagesRef
    // lets the expiry timer decide without re-arming on every page; the
    // guards prevent parallel renewals and renew-loops on a broken backend.
    const loadedPagesRef = useRef(0);
    const renewingRef = useRef(false);
    const sessionCreatedAtRef = useRef(0);
    function resetPages() {
        setLoadedPages(0);
        loadedPagesRef.current = 0;
        pageBlobsRef.current = new Map();
        paintedRef.current = new Set();
        inWindowRef.current = new Set();
        defaultRatioRef.current = null;
        ratioKnownRef.current = new Set();
    }
    // ── 1. Open session ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!materialId) {
            setSession(null);
            resetPages();
            setError(null);
            return;
        }
        let cancelled = false;
        setError(null);
        setSession(null);
        resetPages();
        apiClient(`/materials/${materialId}/secure-session`, { method: 'POST' })
            .then((init) => {
            if (cancelled)
                return;
            sessionCreatedAtRef.current = Date.now();
            setSession(init);
        })
            .catch((err) => {
            if (cancelled)
                return;
            setError(err instanceof ApiError ? err.message : 'No se pudo abrir el documento.');
        });
        return () => { cancelled = true; };
        // resetPages solo toca refs/estado local: estable a efectos de deps.
    }, [materialId]);
    // Renews the viewing session in place. setSession() re-runs the worker
    // effect, which pumps only the pages still missing (decrypted JPEGs are
    // kept). If the renewed session dies within seconds, something is genuinely
    // broken — surface the error instead of looping.
    function renewSession() {
        if (renewingRef.current || !materialId)
            return;
        if (Date.now() - sessionCreatedAtRef.current < MIN_SESSION_LIFETIME_MS) {
            setError('La sesión de visualización ha expirado.');
            return;
        }
        renewingRef.current = true;
        apiClient(`/materials/${materialId}/secure-session`, { method: 'POST' })
            .then((init) => {
            sessionCreatedAtRef.current = Date.now();
            setSession(init);
        })
            .catch((err) => {
            setError(err instanceof ApiError ? err.message : 'La sesión de visualización ha expirado.');
        })
            .finally(() => { renewingRef.current = false; });
    }
    // ── Windowed painting helpers ────────────────────────────────────────────
    const setPageRatio = useCallback((page, w, h) => {
        const ratio = `${w} / ${h}`;
        const wrapper = wrapperRefs.current[page - 1];
        if (wrapper)
            wrapper.style.aspectRatio = ratio;
        ratioKnownRef.current.add(page);
        if (!defaultRatioRef.current) {
            // The first known page sizes every placeholder (PDFs are usually
            // uniform) so the scroll position doesn't jump as pages get painted.
            // Every wrapper carries the inline A4 default, so "unknown" is tracked
            // explicitly rather than by an empty style.
            defaultRatioRef.current = ratio;
            wrapperRefs.current.forEach((el, idx) => {
                if (el && !ratioKnownRef.current.has(idx + 1))
                    el.style.aspectRatio = ratio;
            });
        }
    }, []);
    const releasePage = useCallback((page) => {
        if (!paintedRef.current.has(page))
            return;
        const canvas = canvasRefs.current[page - 1];
        if (canvas) {
            // A 0×0 bitmap frees the backing store; the wrapper's aspect-ratio
            // keeps the layout (and the scroll position) exactly where it was.
            canvas.width = 0;
            canvas.height = 0;
        }
        paintedRef.current.delete(page);
    }, []);
    const ensurePainted = useCallback(async (page) => {
        const blob = pageBlobsRef.current.get(page);
        const canvas = canvasRefs.current[page - 1];
        if (!blob || !canvas || paintedRef.current.has(page))
            return;
        paintedRef.current.add(page);
        try {
            const bmp = await createImageBitmap(blob);
            // Scrolled away while decoding → don't spend the memory.
            if (!inWindowRef.current.has(page)) {
                paintedRef.current.delete(page);
                bmp.close();
                return;
            }
            canvas.width = bmp.width;
            canvas.height = bmp.height;
            canvas.getContext('2d')?.drawImage(bmp, 0, 0);
            setPageRatio(page, bmp.width, bmp.height);
            bmp.close();
        }
        catch (err) {
            paintedRef.current.delete(page);
            setError(err instanceof Error ? err.message : 'Error renderizando página');
        }
    }, [setPageRatio]);
    // ── 2. Worker + per-page fetch+decrypt ───────────────────────────────────
    useEffect(() => {
        if (!session || !materialId)
            return;
        let stopped = false;
        const worker = new Worker(new URL('../workers/secure-decrypt.worker.ts', import.meta.url), { type: 'module' });
        workerRef.current = worker;
        worker.onmessage = (e) => {
            const msg = e.data;
            if (msg.type === 'inited') {
                // Worker is ready — request every page still missing, but cap
                // concurrency so a long PDF doesn't fire dozens of simultaneous GETs
                // (which trips the API rate limiter). Pages load progressively and in
                // order. After a session renewal the already-decrypted pages are
                // skipped: only the gaps get downloaded.
                const MAX_CONCURRENT = 4;
                let next = 1;
                const pump = () => {
                    if (stopped)
                        return;
                    while (next <= session.pageCount && pageBlobsRef.current.has(next))
                        next++;
                    if (next > session.pageCount)
                        return;
                    const p = next++;
                    fetchAndQueuePage(materialId, session.sessionId, p, worker)
                        .catch((err) => {
                        if (stopped)
                            return;
                        // Sesión caducada/evacuada a mitad de descarga (p. ej. reinicio
                        // del servidor): renovar en silencio en vez de expulsar.
                        const code = err instanceof ApiError ? err.code : undefined;
                        if (code === 'SECURE_SESSION_EXPIRED' ||
                            code === 'SECURE_SESSION_EVICTED' ||
                            code === 'SECURE_SESSION_NOT_FOUND') {
                            renewSession();
                            return;
                        }
                        setError(err instanceof Error ? err.message : 'Error al descargar páginas');
                    })
                        .finally(() => { if (!stopped)
                        pump(); });
                };
                for (let i = 0; i < Math.min(MAX_CONCURRENT, session.pageCount); i++)
                    pump();
                return;
            }
            if (msg.type === 'decrypted') {
                pageBlobsRef.current.set(msg.page, new Blob([msg.jpeg], { type: 'image/jpeg' }));
                setLoadedPages((n) => Math.max(n, msg.page));
                loadedPagesRef.current = Math.max(loadedPagesRef.current, msg.page);
                if (inWindowRef.current.has(msg.page))
                    void ensurePainted(msg.page);
                return;
            }
            if (msg.type === 'error') {
                setError(msg.message);
            }
        };
        // Initialise with the master key.
        worker.postMessage({ type: 'init', masterKey: session.masterKey });
        return () => {
            stopped = true;
            worker.terminate();
            workerRef.current = null;
        };
        // renewSession queda fuera de deps a propósito: solo usa refs y materialId.
    }, [session, materialId, ensurePainted]);
    // ── 3. Paint window: observe which pages are near the viewport ──────────
    useEffect(() => {
        if (!session || typeof IntersectionObserver === 'undefined') {
            // No observer support → paint everything (old behaviour).
            if (session) {
                for (let p = 1; p <= session.pageCount; p++)
                    inWindowRef.current.add(p);
            }
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const page = Number(entry.target.dataset.page);
                if (!page)
                    continue;
                if (entry.isIntersecting) {
                    inWindowRef.current.add(page);
                    void ensurePainted(page);
                }
                else {
                    inWindowRef.current.delete(page);
                    releasePage(page);
                }
            }
        }, 
        // ±1.5 viewport heights: the current page plus a neighbour each side
        // stay painted, so normal scrolling never shows a blank page.
        { root: containerRef.current, rootMargin: '150% 0px' });
        for (const el of wrapperRefs.current)
            if (el)
                observer.observe(el);
        return () => observer.disconnect();
    }, [session, ensurePainted, releasePage]);
    // Coming back from the background the browser may have purged canvas
    // bitmaps (iOS Safari does under memory pressure): repaint the window.
    useEffect(() => {
        if (!session)
            return;
        function onVisible() {
            if (document.visibilityState !== 'visible')
                return;
            for (const page of inWindowRef.current) {
                paintedRef.current.delete(page);
                void ensurePainted(page);
            }
        }
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [session, ensurePainted]);
    // ── Silent renewal shortly before session expiry ─────────────────────────
    // A fully-loaded document needs no session at all (every page's JPEG is
    // already in memory), so reading can continue indefinitely. Only a
    // partially-loaded document renews, 60s before expiry, so in-flight page
    // fetches never fail.
    useEffect(() => {
        if (!session)
            return;
        const remaining = new Date(session.expiresAt).getTime() - Date.now();
        const t = setTimeout(() => {
            if (loadedPagesRef.current >= session.pageCount)
                return;
            renewSession();
        }, Math.max(0, remaining - 60_000));
        return () => clearTimeout(t);
        // renewSession queda fuera de deps a propósito: solo usa refs y materialId.
    }, [session]);
    // ── Lock body scroll while open ──────────────────────────────────────────
    useEffect(() => {
        if (!materialId)
            return;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [materialId]);
    // ── Hard lockdown: keys, clipboard, context menu, print, drag ────────────
    useEffect(() => {
        if (!materialId)
            return;
        const blockedKeys = new Set(['s', 'p', 'c', 'x', 'a', 'u']);
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && blockedKeys.has(e.key.toLowerCase())) {
                e.preventDefault();
                e.stopPropagation();
            }
            if (e.key === 'PrintScreen')
                e.preventDefault();
            // F12 / DevTools shortcut — best effort.
            if (e.key === 'F12')
                e.preventDefault();
        }
        function onContextMenu(e) { e.preventDefault(); }
        function onCopy(e) { e.preventDefault(); }
        function onCut(e) { e.preventDefault(); }
        function onDragStart(e) { e.preventDefault(); }
        function onBeforePrint() { document.body.classList.add('apolo-blocking-print'); }
        function onAfterPrint() { document.body.classList.remove('apolo-blocking-print'); }
        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('contextmenu', onContextMenu, true);
        window.addEventListener('copy', onCopy, true);
        window.addEventListener('cut', onCut, true);
        window.addEventListener('dragstart', onDragStart, true);
        window.addEventListener('beforeprint', onBeforePrint);
        window.addEventListener('afterprint', onAfterPrint);
        return () => {
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('contextmenu', onContextMenu, true);
            window.removeEventListener('copy', onCopy, true);
            window.removeEventListener('cut', onCut, true);
            window.removeEventListener('dragstart', onDragStart, true);
            window.removeEventListener('beforeprint', onBeforePrint);
            window.removeEventListener('afterprint', onAfterPrint);
            document.body.classList.remove('apolo-blocking-print');
        };
    }, [materialId, onClose]);
    // ── Track container width to constrain canvas size ───────────────────────
    useEffect(() => {
        if (!materialId)
            return;
        function update() {
            if (!containerRef.current)
                return;
            setContainerWidth(Math.min(containerRef.current.clientWidth - 32, 1100));
        }
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [materialId]);
    if (!materialId)
        return null;
    return createPortal(_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-label": `Visor seguro del documento ${title}`, className: "apolo-secure-viewer fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm", style: { userSelect: 'none', WebkitUserSelect: 'none' }, onContextMenu: (e) => e.preventDefault(), children: [_jsxs("div", { className: "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 border-b px-2 sm:px-4 py-2", style: { background: 'var(--surface)', borderColor: 'var(--border)' }, children: [_jsx("p", { className: "flex-1 truncate text-xs sm:text-sm font-semibold", style: { color: 'var(--fg)' }, children: title }), session && (_jsxs("span", { className: "text-xs", style: { color: 'var(--fg-2)' }, children: [Math.min(loadedPages, session.pageCount), " / ", session.pageCount, " p\u00E1ginas"] })), _jsx("button", { type: "button", onClick: onClose, "aria-label": "Cerrar visor", className: "flex-shrink-0 rounded-lg p-1.5 transition-colors", style: { color: 'var(--fg-2)' }, onMouseEnter: (e) => (e.currentTarget.style.background = 'var(--hover)'), onMouseLeave: (e) => (e.currentTarget.style.background = 'transparent'), children: _jsxs("svg", { viewBox: "0 0 20 20", className: "h-5 w-5", fill: "none", stroke: "currentColor", strokeWidth: 2, children: [_jsx("line", { x1: "5", y1: "5", x2: "15", y2: "15" }), _jsx("line", { x1: "15", y1: "5", x2: "5", y2: "15" })] }) })] }), _jsxs("div", { ref: containerRef, className: "flex flex-1 justify-center overflow-y-auto py-6", children: [!session && !error && (_jsxs("div", { className: "m-auto flex items-center gap-2 text-sm text-white", children: [_jsx(Spinner, { size: "md" }), _jsx("span", { children: "Abriendo documento\u2026" })] })), error && (_jsxs("div", { className: "m-auto rounded-xl border p-6 text-center", style: { background: 'var(--surface)', borderColor: 'var(--border)' }, children: [_jsx("p", { className: "text-sm font-semibold", style: { color: '#dc2626' }, children: error }), _jsx("p", { className: "mt-1 text-xs", style: { color: 'var(--fg-2)' }, children: "Cierra el visor e int\u00E9ntalo de nuevo." })] })), session && !error && (_jsx("div", { className: "flex flex-col items-center gap-4", children: Array.from({ length: session.pageCount }, (_, i) => (
                        // The wrapper owns the layout (width + aspect-ratio placeholder);
                        // the canvas inside may be 0×0 while off-window. The aspect-ratio
                        // is set on the DOM node directly (setPageRatio) so React never
                        // overwrites it on re-render.
                        _jsx("div", { ref: (el) => { wrapperRefs.current[i] = el; }, "data-page": i + 1, className: "bg-white shadow-2xl", style: { width: containerWidth, maxWidth: '100%', aspectRatio: DEFAULT_PAGE_RATIO }, "aria-label": `Página ${i + 1}`, children: _jsx("canvas", { ref: (el) => { canvasRefs.current[i] = el; }, className: "block h-full w-full" }) }, `page-${i + 1}`))) }))] })] }), document.body);
}
async function fetchAndQueuePage(materialId, sessionId, page, worker) {
    const enc = await apiClient(`/materials/${materialId}/secure-page/${page}?session=${encodeURIComponent(sessionId)}`);
    worker.postMessage({ type: 'decrypt', page, ...enc });
}
//# sourceMappingURL=SecureMaterialViewer.js.map